import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

export const ValuePropsSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const values = [
    {
      title: "Holistic Advisory & Deal Execution",
      desc: "Comprehensive guidance grounded in real-time market intelligence, enabling informed decisions and safe execution."
    },
    {
      title: "Direct Access to Global Network",
      desc: "Structuring and securing tailored solutions through our network of specialists and stakeholders when traditional channels fail."
    },
    {
      title: "Technical Expertise That De-Risks Decisions",
      desc: "Rigorous technical due diligence ensuring every project is supported by sound operational, logistic, and economic fundamentals."
    }
  ];

  return (
    <section id="value-props" ref={containerRef} className="py-24 bg-zinc-900 text-white">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h4 
            className="text-amber-500 font-bold uppercase tracking-widest text-sm mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Why UGW Global
          </motion.h4>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Value Proposition
          </motion.h2>
          <motion.p 
            className="text-zinc-400 text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            UGW GLOBAL stands apart through a three-pillar value model that blends market expertise with practical execution.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="bg-zinc-800 p-8 rounded-sm border border-zinc-700 hover:border-amber-500 transition-colors duration-300"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <div className="text-amber-500 mb-6">
                <span className="text-5xl font-bold opacity-20 absolute">0{index + 1}</span>
                <CheckCircle2 className="w-8 h-8 relative z-10 mt-2" />
              </div>
              <h3 className="text-xl font-bold mb-4 relative z-10">{value.title}</h3>
              <p className="text-zinc-400 leading-relaxed relative z-10">
                {value.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="mt-20 p-8 bg-gradient-to-r from-amber-600 to-amber-500 rounded-sm text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-zinc-900 mb-4">What This Means for You</h3>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-zinc-900 font-medium">
            <span>Faster Decision-Making</span>
            <span className="hidden md:inline">•</span>
            <span>Reduced Risk</span>
            <span className="hidden md:inline">•</span>
            <span>Stronger Commercial Outcomes</span>
            <span className="hidden md:inline">•</span>
            <span>Single Point of Contact</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};